import { computed, ref, type Ref } from 'vue'

export type SortDirection = 'asc' | 'desc'

export type ListSort<TField extends string = string> = {
  field: TField
  direction: SortDirection
}

export type UsePaginatedListOptions<TField extends string = string> = {
  initialPageIndex?: number
  initialPageSize?: number
  initialTotalItems?: number
  initialSort?: ListSort<TField> | null
}

export const usePaginatedList = <TField extends string = string>(options: UsePaginatedListOptions<TField> = {}) => {
  const pageIndex = ref(options.initialPageIndex ?? 0)
  const pageSize = ref(options.initialPageSize ?? 25)
  const totalItems = ref(options.initialTotalItems ?? 0)
  const sort = ref(options.initialSort ?? null) as Ref<ListSort<TField> | null>

  const currentPage = computed(() => pageIndex.value + 1)

  const totalPages = computed(() => {
    if (pageSize.value <= 0) return 1
    if (!totalItems.value) return 1
    return Math.max(1, Math.ceil(totalItems.value / pageSize.value))
  })

  const pageStart = computed(() => {
    if (!totalItems.value) return 0
    if (pageSize.value <= 0) return 1
    return pageIndex.value * pageSize.value + 1
  })

  const pageStop = computed(() => {
    if (!totalItems.value) return 0
    if (pageSize.value <= 0) return totalItems.value
    return Math.min((pageIndex.value + 1) * pageSize.value, totalItems.value)
  })

  const isPreviousPageDisabled = computed(() => pageIndex.value <= 0 || totalPages.value <= 1)
  const isNextPageDisabled = computed(() => currentPage.value >= totalPages.value || totalPages.value <= 1)

  const getLastPageIndex = () => {
    if (pageSize.value <= 0 || !totalItems.value) return 0
    return Math.max(0, Math.ceil(totalItems.value / pageSize.value) - 1)
  }

  const clampPageIndex = () => {
    const nextPageIndex = Math.min(pageIndex.value, getLastPageIndex())
    if (pageIndex.value === nextPageIndex) return false

    pageIndex.value = nextPageIndex
    return true
  }

  const setPageIndex = (value: number) => {
    const nextPageIndex = Math.min(Math.max(0, Math.floor(value)), getLastPageIndex())
    if (pageIndex.value === nextPageIndex) return false

    pageIndex.value = nextPageIndex
    return true
  }

  const setPageSize = (value: number) => {
    if (pageSize.value === value) return false

    pageSize.value = value
    pageIndex.value = 0
    return true
  }

  const setTotalItems = (value: number) => {
    totalItems.value = Math.max(0, value)
    return clampPageIndex()
  }

  const setSort = (value: ListSort<TField> | null) => {
    if (sort.value?.field === value?.field && sort.value?.direction === value?.direction) return false

    sort.value = value ? { ...value } : null
    pageIndex.value = 0
    return true
  }

  const toggleSort = (field: TField) => {
    const nextDirection = sort.value?.field === field && sort.value.direction === 'asc' ? 'desc' : 'asc'
    return setSort({ field, direction: nextDirection })
  }

  const previousPage = () => {
    if (isPreviousPageDisabled.value) return false
    return setPageIndex(pageIndex.value - 1)
  }

  const nextPage = () => {
    if (isNextPageDisabled.value) return false
    return setPageIndex(pageIndex.value + 1)
  }

  return {
    pageIndex,
    pageSize,
    totalItems,
    sort,
    currentPage,
    totalPages,
    pageStart,
    pageStop,
    isPreviousPageDisabled,
    isNextPageDisabled,
    setPageIndex,
    setPageSize,
    setTotalItems,
    setSort,
    toggleSort,
    previousPage,
    nextPage,
  }
}
